import {
  EMableCustomEventName,
  EMableEventName,
} from '@/core/constants/event_types/events';
import { TGtagUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/types';

export type GAdsConversionPayload =
  | GAdsConversionPayloadWithoutTransactionId
  | GAdsConversionPayloadWithTransactionId;

export type GadsPayloadRetailItem = {
  google_business_vertical: 'retail';
  id: string;
};
export type GAdsConversionPayloadWithTransactionId = {
  currency: string;
  items: GadsPayloadRetailItem[];
  // send_to: string;
  transaction_id: string;
  value: number | string;
};

export type GAdsConversionPayloadWithoutTransactionId = {
  currency: string;
  items: GadsPayloadRetailItem[];
  // send_to: string;
  value: number | string;
};

export type GoogleAdsDestinationPayload = {
  conversion_label: string;
  event_name: GadsPayloadEventName;
  event_payload: GAdsConversionPayload;
  /**
   * Google Ads Specific user data ( for enhanced conversion events )
   */
  gads_user_data: TGtagUserData;
  /**
   * The market ID for the event
   * optional, if not provided, we use the primary market ID
   */
  marketID?: string;
};

export enum GadsPayloadEventName {
  ADD_PAYMENT_INFO = 'add_payment_info',
  ADD_TO_CART = 'add_to_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  CUSTOM = 'custom',
  CUSTOM_ALL_CUSTOMER_PURCHASE = 'custom_all_customer_purchase',
  CUSTOM_NEW_CUSTOMER_PURCHASE = 'custom_new_customer_purchase',
  CUSTOM_RETURNING_CUSTOMER_PURCHASE = 'custom_returning_customer_purchase',
  LEAD = 'lead',
  PAGE_VIEW = 'page_view',
  PURCHASE = 'purchase',
  VIEW_ITEM = 'view_item',
  VIEW_ITEM_LIST = 'view_item_list',
}

export const EventsWithCartData = [
  EMableEventName.CHECKOUT_STARTED,
  EMableEventName.ORDER_COMPLETED,
  EMableEventName.PAYMENT_INFO_ADDED,
];

export const EventsWithProductData: EMableEventName[] = [
  EMableEventName.PRODUCT_ADDED,
  EMableEventName.PRODUCT_VIEWED,
  EMableEventName.ORDER_COMPLETED,
  EMableEventName.CHECKOUT_STARTED,
  EMableEventName.COLLECTION_VIEWED,
  EMableEventName.PAYMENT_INFO_ADDED,
];

export type TSupportedGadsEventNames =
  | EMableCustomEventName.AllCustomerPurchase
  | EMableCustomEventName.NewCustomerPurchase
  | EMableCustomEventName.ReturningCustomerPurchase
  | EMableEventName.CHECKOUT_STARTED
  | EMableEventName.COLLECTION_VIEWED
  | EMableEventName.CUSTOM_EVENT
  | EMableEventName.LEAD
  | EMableEventName.ORDER_COMPLETED
  | EMableEventName.PAGE_VIEWED
  | EMableEventName.PAYMENT_INFO_ADDED
  | EMableEventName.PRODUCT_ADDED
  | EMableEventName.PRODUCT_VIEWED;

export const mableToGoogleAdsEventMappings: Record<
  TSupportedGadsEventNames,
  GadsPayloadEventName
> = {
  [EMableCustomEventName.AllCustomerPurchase]:
    GadsPayloadEventName.CUSTOM_ALL_CUSTOMER_PURCHASE,
  [EMableCustomEventName.NewCustomerPurchase]:
    GadsPayloadEventName.CUSTOM_NEW_CUSTOMER_PURCHASE,
  [EMableCustomEventName.ReturningCustomerPurchase]:
    GadsPayloadEventName.CUSTOM_RETURNING_CUSTOMER_PURCHASE,
  [EMableEventName.CHECKOUT_STARTED]: GadsPayloadEventName.BEGIN_CHECKOUT,
  [EMableEventName.COLLECTION_VIEWED]: GadsPayloadEventName.VIEW_ITEM_LIST,
  [EMableEventName.CUSTOM_EVENT]: GadsPayloadEventName.CUSTOM,
  [EMableEventName.LEAD]: GadsPayloadEventName.LEAD,
  [EMableEventName.ORDER_COMPLETED]: GadsPayloadEventName.PURCHASE,
  [EMableEventName.PAGE_VIEWED]: GadsPayloadEventName.PAGE_VIEW,
  [EMableEventName.PAYMENT_INFO_ADDED]: GadsPayloadEventName.ADD_PAYMENT_INFO,
  [EMableEventName.PRODUCT_ADDED]: GadsPayloadEventName.ADD_TO_CART,
  [EMableEventName.PRODUCT_VIEWED]: GadsPayloadEventName.VIEW_ITEM,
};

export const calculateGAdsEventMapping = (
  eventName: EMableEventName,
  customEventName?: EMableCustomEventName,
) => {
  const name = eventName as TSupportedGadsEventNames;
  let mappedGadsEventName = mableToGoogleAdsEventMappings[name];
  if (name === EMableEventName.CUSTOM_EVENT && customEventName) {
    mappedGadsEventName =
      mableToGoogleAdsEventMappings[
        customEventName as TSupportedGadsEventNames
      ];
  }
  return mappedGadsEventName;
};
